import { deepMergeDefaults, isPlainObject } from '@frontend/duck-tape';
import { useFeatureValue as gbUseFeatureValue } from '@growthbook/growthbook-react';
import type { FeatureMap } from './helpers';

type FeatureValue<K extends keyof ExcludeKeysOfType<FeatureMap, boolean>> = ExcludeKeysOfType<FeatureMap, boolean>[K];

/**
 * Like `useFeatureValue`, but for object-shaped flags where the GrowthBook value
 * may arrive partial or malformed. Fills gaps in the remote value with the
 * provided local defaults so consumers always get the full expected shape.
 */
export const useGapFilledFeatureValue = <
  K extends keyof ExcludeKeysOfType<FeatureMap, boolean>,
  V extends FeatureValue<K> | null = null,
>(
  flag: K,
  defaultValue?: V,
): { value: V extends null ? MaybeNull<FeatureValue<K>> : FeatureValue<K> } => {
  const value = gbUseFeatureValue(flag, defaultValue ?? null);

  const merged =
    defaultValue && isPlainObject(defaultValue)
      ? isPlainObject(value)
        ? deepMergeDefaults(defaultValue as Record<string, unknown>, value)
        : defaultValue
      : value;

  return { value: merged as V extends null ? MaybeNull<FeatureValue<K>> : FeatureValue<K> };
};
